/**
 * Shareable Trade Utilities
 * Encode/decode trade data for shareable links
 */

/**
 * Encode trade data into a URL-safe string
 * @param {Object} trade - Trade object
 * @returns {string} Encoded string
 */
export const encodeTradeData = (trade) => {
  const shareData = {
    mi: trade.market_index, // market_index
    op: trade.open_price, // open_price
    cp: trade.trade_info?.closed_price || trade.close_price, // close_price
    ca: trade.collateral_amount, // collateral_amount
    lv: trade.leverage, // leverage
    lg: trade.long ? 1 : 0, // is_long (1 or 0)
    ti: trade.user_trade_index, // trade_index
    ts: trade.trade_info?.closed_at || trade.closed_at || Date.now() / 1000, // timestamp
    // Optional: referral code for future use
    ref: trade.referral_code || null,
  };

  // Remove null values
  Object.keys(shareData).forEach(key => {
    if (shareData[key] === null || shareData[key] === undefined) {
      delete shareData[key];
    }
  });

  try {
    const jsonString = JSON.stringify(shareData);
    // Use base64url encoding (URL-safe)
    const encoded = btoa(jsonString)
      .replace(/\+/g, '-')
      .replace(/\//g, '_')
      .replace(/=+$/, '');
    return encoded;
  } catch (error) {
    console.error('Error encoding trade data:', error);
    return null;
  }
};

/**
 * Decode trade data from URL-safe string
 * @param {string} encoded - Encoded string
 * @returns {Object|null} Decoded trade object or null
 */
export const decodeTradeData = (encoded) => {
  try {
    // Restore base64 padding and special chars
    let base64 = encoded
      .replace(/-/g, '+')
      .replace(/_/g, '/');
    
    // Add padding if needed
    const pad = base64.length % 4;
    if (pad) {
      base64 += '='.repeat(4 - pad);
    }

    const jsonString = atob(base64);
    const shareData = JSON.parse(jsonString);

    // Reconstruct trade object
    return {
      market_index: shareData.mi,
      open_price: shareData.op,
      close_price: shareData.cp,
      trade_info: {
        closed_price: shareData.cp,
        closed_at: shareData.ts,
      },
      collateral_amount: shareData.ca,
      leverage: shareData.lv,
      long: shareData.lg === 1,
      user_trade_index: shareData.ti,
      referral_code: shareData.ref || null,
    };
  } catch (error) {
    console.error('Error decoding trade data:', error);
    return null;
  }
};

/**
 * Generate shareable URL for a trade
 * @param {Object} trade - Trade object
 * @param {string} baseUrl - Base URL (optional, defaults to current origin)
 * @returns {string} Full shareable URL
 */
export const generateShareableUrl = (trade, baseUrl = null) => {
  const encoded = encodeTradeData(trade);
  if (!encoded) return null;

  const origin = baseUrl || window.location.origin;
  return `${origin}/share/trade/${encoded}`;
};

/**
 * Extract encoded data from shareable URL
 * @param {string} url - Full URL or just the encoded part
 * @returns {string|null} Encoded data string
 */
export const extractEncodedFromUrl = (url) => {
  try {
    // If it's a full URL, extract the encoded part
    if (url.includes('/share/trade/')) {
      const parts = url.split('/share/trade/');
      return parts[1]?.split('?')[0] || null;
    }
    // Otherwise assume it's just the encoded string
    return url;
  } catch (error) {
    return null;
  }
};

